<template>
  <div class="min-h-screen bg-gray-50 pt-6 pb-28" dir="rtl">
    <div class="max-w-4xl mx-auto md:max-w-lg space-y-6">
      <input id="fileUpload" accept=".jpg,.jpeg,.png,.JPG,.JPEG,.gif" type="file" hidden
             v-on:change="input_file_changed">

      <!-- Existing Files -->
      <div v-if="JSON.parse(items).length > 0">
        <h3 class="text-base font-bold text-gray-900 mb-3">فایل‌های ارسال شده</h3>
        <div class="grid grid-cols-4 gap-3">
          <div
            v-for="(item, i) in JSON.parse(items)"
            :key="i"
            @click="open_image('https://'+item.ArchivePath)"
            class="cursor-pointer rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 transition-all duration-200 hover:shadow-lg"
          >
            <img
              :src="'https://'+item.ArchivePath"
              class="w-full h-24 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <!-- Add File Button -->
      <div 
        class="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-all duration-200"
        @click="select_file()"
      >
        <div class="flex items-center justify-between group">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <span class="text-gray-900 font-medium">{{ $t('add_file') }}</span>
          </div>
          <svg class="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </div>
      </div>

      
      <!-- New Files to Upload -->
      <div v-if="files.length > 0">
        <h3 class="text-base font-bold text-gray-900 mb-3">فایل‌های جدید</h3>
        <div class="grid grid-cols-4 gap-3">
          <div 
            v-for="(item, i) in files"
            :key="i"
            class="relative group"
          >
            <div class="relative rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 transition-all duration-200">
              <img 
                :src="item.file" 
                class="w-full h-24 object-cover"
                loading="lazy"
              />
              <!-- Delete Button -->
              <button
                @click="delete_file(i)"
                class="absolute top-2 left-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors duration-200"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p v-if="item.title" class="text-xs text-gray-600 mt-1 truncate text-center">{{ item.title }}</p>
          </div>
        </div>
      </div>


      <!-- Empty State -->
      <div v-if="files.length === 0 && JSON.parse(items).length === 0" class="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
        <div class="max-w-2xl mx-auto">
          <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </div>
          <p class="text-gray-500 font-medium mb-2">فایلی وجود ندارد</p>
          <p class="text-sm text-gray-400">برای افزودن فایل روی دکمه بالا کلیک کنید</p>
        </div>
      </div>

      <!-- Send Files Button -->
      <button 
        v-if="files.length > 0"
        @click="save()"
        class="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-6 rounded-xl font-bold transition-all duration-200"
      >
        {{$t('sendFiles')}}
      </button>

      <!-- Camera Modal -->
      <div v-if="dialog_camera" class="fixed inset-0 bg-black z-999 flex flex-col" dir="ltr">
        <!-- Header -->
        <div class="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
          <button
            @click="dialog_camera = false"
            class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-blue-700 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 class="text-lg font-bold">{{$t('camera')}}</h3>
          <button
            @click="downloadImage()"
            class="px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {{$t('save')}}
          </button>
        </div>

        <!-- Camera Content -->
        <div class="flex-1 flex flex-col items-center justify-center bg-gray-900 p-4">
          <div v-if="isCameraOpen" class="camera-box w-full flex items-center justify-center">
            <video v-show="!isPhotoTaken" ref="camera" width="450" height="337.5" autoplay style="max-width: 100%; height: auto;"></video>
            <canvas v-show="isPhotoTaken" id="photoTaken" ref="canvas" width="450" height="337.5" style="max-width: 100%; height: auto;"></canvas>
          </div>
          
          <!-- Camera Button -->
          <div v-if="isCameraOpen" class="mt-6">
            <button 
              type="button" 
              @click="takePhoto"
              class="w-20 h-20 rounded-full bg-white border-4 border-gray-300 hover:border-blue-400 transition-all duration-200 flex items-center justify-center hover:shadow-xl"
            >
              <svg class="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {bus} from '../../../main'

export default {
  name: 'Attachments',
  data() {
    return {
      files: [],
      items: "[]",
      dialog_camera: false,
      isCameraOpen: false,
      isPhotoTaken: false
    }
  },
  mounted() {
    this.get_attachments()

    // console.log("rec id",this.$route.params.rec_id)
  },
  watch: {},
  props:{
    master_id:String,
    doc_type:String,
  },
  methods: {
    open_image(img) {
      window.open(img, '_blank')
    },
    save() {
      if (this.files.length) {
        this.upload_image(0)

      } else {
        this.$router.go(-1)
      }
    },
    select_file() {

      document.getElementById('fileUpload').click()
    },
    open_camera() {
      this.isCameraOpen = true
      this.createCameraElement()
    },
    delete_file(index) {
      this.files.splice(index, 1)
    },
    takePhoto() {
      this.isPhotoTaken = true

      const context = this.$refs.canvas.getContext('2d')
      context.drawImage(this.$refs.camera, 0, 0, 450, 337.5)
    },
    downloadImage() {
      if (this.isPhotoTaken == true) {
        this.isCameraOpen = false
        // const download = document.getElementById("downloadPhoto");
        const preview = document.getElementById('photoTaken')
        const canvas = document.getElementById('photoTaken').toDataURL('image/jpeg')
        // .replace("image/jpeg", "image/octet-stream");
        var obj = {file: canvas, preview: preview, upload: 0}
        this.files.push(obj)
        this.dialog_camera = false
        this.isPhotoTaken = false
      }
      // download.setAttribute("href", canvas);
    },
    input_file_changed() {
      // console.log("input_file_changed")
      var self = this

      var reader = new FileReader()
      var f = document.getElementById('fileUpload').files
      // if (f != null) {
      reader.onloadend = function () {
        // console.log(reader.result);
        if (!reader.result.startsWith('data:image/webp')) {
          self.resizeBase64Img(reader.result, 700, 700).then(function (newImg) {
            // $("body").append(newImg[0]);
            var obj = {file: newImg[0].src, preview: reader.result, upload: 0}
            self.files.push(obj)
            reader = null;
            f = null;
            document.getElementById('fileUpload').files = null;
            obj = null;
          })
          // console.log(obj)
        } else {
          bus.$emit('show_toast', [1500, self.$t('fileIsNotGranted')])
        }
      };
      reader.readAsDataURL(f[0])

      // }

    },

    createCameraElement() {
      try {
        const constraints = (window.constraints = {
          audio: false,
          video: true
        })

        navigator.mediaDevices
          .getUserMedia(constraints)
          .then(stream => {
            this.$refs.camera.srcObject = stream
          })
          .catch(error => {
            alert(this.$t('deviceHasNoCamera'))
          })
      } catch (err) {
        alert(err)
      }
    },
    async resizeBase64Img(base64, minimum) {
      // try {

      var canvas = document.createElement('canvas')

      var deferred = $.Deferred()
      $('<img/>').attr('src', base64).load(function () {
        var last_width = this.width
        var last_height = this.height

        if (last_width > last_height) {
          last_width = (last_width * minimum) / last_height
          last_height = minimum
        } else {
          last_height = (last_height * minimum) / last_width
          last_width = minimum
        }
        canvas.width = last_width
        canvas.height = last_height
        var context = canvas.getContext('2d')

        // console.log(this.width, this.height)
        context.scale(last_width / this.width, last_height / this.height)
        context.drawImage(this, 0, 0)

        deferred.resolve($('<img/>').attr('src', canvas.toDataURL()))
      })
      return deferred.promise()
      // } catch (e) {
      //   bus.$emit('show_toast', [3000, "error on Base64Img"])
      //
      // }
    },
    upload_image(index) {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      var form_data = new FormData()
      form_data.append('UserId', user_id)
      form_data.append('ProcessId', this.$route.params.rec_id)
      form_data.append('Format', 'png')
      form_data.append('ReqType', 13)
      form_data.append('DocType', 1)
      var my_file = this.files[index].file

      var file = this.dataURLtoFile(my_file, 'attach.png')
      form_data.append('Picture', file)

      this.axios.post('UploadFile', form_data, {})
        .then(response => {
          // self.send_signature(response.data.ArchiveId,response.data.ImageName);
          if (response.data.error == 0) {
            self.files[index].upload = 1
            self.files[index].ArchiveId = response.data.ArchiveId
            self.files[index].ImageName = response.data.ImageName
            if (index < self.files.length - 1) {
              self.upload_image(index + 1)
            } else {
              self.send_attach(0)
            }

          } else {
            self.files[index].upload = 2
            bus.$emit('show_toast', [1500, response.data.msg])
          }
        }).catch(e => {
        this.errors.push(e)
      })
    },
    dataURLtoFile(dataurl, filename) {
      try {

        var arr = dataurl.split(',')
        var mime = arr[0].match(/:(.*?);/)[1]
        var bstr = atob(arr[1])
        var n = bstr.length
        var u8arr = new Uint8Array(n)

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }

        return new File([u8arr], filename, {type: mime})
      } catch (e) {
        bus.$emit('show_toast', [3000, "error on dataURLtoFile"])

      }
    },

    send_attach(index_arr) {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      // console.log();

      var attachment_array = []

      self.files.forEach(function (number) {
        var pre_obj = {
          ArchiveId: number.ArchiveId,
          ImageName: number.ImageName,
          Summary: '',
        }
        attachment_array.push(pre_obj)
      })

      const send_data = {
        ArchiveId: attachment_array[index_arr].ArchiveId,
        FileName: attachment_array[index_arr].ImageName,
        UserId: user_id,
        DocCode: this.doc_type,
        MasterId: this.master_id,
      }
      // this.axios.post('/Register' , send_data,{
      this.axios.post('/AddAccDocFile', send_data, {})
        .then(response => {
          if (response.data.error == 0) {
            if(index_arr < (self.files.length-1)){
              self.send_attach(index_arr+1)
            }else{
              self.$router.go(-1)
            }
          } else {
            bus.$emit('show_toast', [2000, response.data.msg + " " + JSON.stringify(send_data)])
          }
        }).catch(e => {
        this.errors.push(e)
      })
    },
    get_attachments() {
      var self = this
      var user_id = JSON.parse(localStorage.getItem('profile')).UserId
      // console.log();

      var attachment_array = []

      const send_data = {
        UserId: user_id,
        MasterId: this.master_id,
      }

      // this.axios.post('/Register' , send_data,{
      this.axios.post('/GetKardexMasterFiles_ById', send_data, {})
        .then(response => {
          if (response.data.error == 0) {
           self.items = response.data.dataa
          } else {
            bus.$emit('show_toast', [2000, response.data.msg + " " + JSON.stringify(send_data)])
          }
        }).catch(e => {
        this.errors.push(e)
      })
    }

  }
}
</script>

<style scoped>
.camera-box {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-shoot {
  display: flex;
  align-items: center;
  justify-content: center;
}

.camera-download {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
}
</style>
